import { useEffect, useSyncExternalStore } from 'react';

import { DARK_THEME_CLASSNAME } from 'constants.ts';

export enum COLOR_MODE {
    light = 'light',
    dark = 'dark',
}

const STORAGE_KEY = 'color-theme';
const DARK_SCHEME_QUERY = '(prefers-color-scheme: dark)';

const listeners = new Set<() => void>();

const darkSchemeQueryList =
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
        ? window.matchMedia(DARK_SCHEME_QUERY)
        : null;

export const resolveColorMode = (stored: string | null, prefersDark: boolean): COLOR_MODE => {
    if (stored === COLOR_MODE.dark || stored === JSON.stringify(COLOR_MODE.dark)) {
        return COLOR_MODE.dark;
    }
    if (stored === COLOR_MODE.light || stored === JSON.stringify(COLOR_MODE.light)) {
        return COLOR_MODE.light;
    }

    return prefersDark ? COLOR_MODE.dark : COLOR_MODE.light;
};

const readStoredChoice = (): string | null => {
    try {
        return window.localStorage.getItem(STORAGE_KEY);
    } catch {
        return null;
    }
};

const readMode = (): COLOR_MODE =>
    resolveColorMode(readStoredChoice(), darkSchemeQueryList?.matches ?? false);

const subscribe = (listener: () => void) => {
    listeners.add(listener);
    window.addEventListener('storage', listener);
    darkSchemeQueryList?.addEventListener('change', listener);
    return () => {
        listeners.delete(listener);
        window.removeEventListener('storage', listener);
        darkSchemeQueryList?.removeEventListener('change', listener);
    };
};

const store = (mode: COLOR_MODE) => {
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(mode));
    } catch (error) {
        console.log(error);
    }
    for (const listener of listeners) listener();
};

const applyWithoutTransitions = (apply: () => void) => {
    const style = document.createElement('style');
    style.textContent = '*,*::before,*::after{transition:none!important}';
    document.head.appendChild(style);
    apply();
    window.getComputedStyle(document.body).getPropertyValue('opacity');
    requestAnimationFrame(() => style.remove());
};

const useColorMode = (forcedMode?: `${COLOR_MODE}`) => {
    const mode = useSyncExternalStore(subscribe, readMode, () => COLOR_MODE.light);
    const isDark = (forcedMode ?? mode) === COLOR_MODE.dark;

    const setMode = (next: COLOR_MODE) => {
        if (forcedMode) return;
        store(next);
    };

    const toggleMode = () => {
        setMode(mode === COLOR_MODE.dark ? COLOR_MODE.light : COLOR_MODE.dark);
    };

    useEffect(() => {
        const { classList } = window.document.body;
        if (classList.contains(DARK_THEME_CLASSNAME) === isDark) return;
        applyWithoutTransitions(() => classList.toggle(DARK_THEME_CLASSNAME, isDark));
    }, [isDark]);

    return { isDark, toggleMode, setMode };
};

export default useColorMode;
